import { useEffect, useState } from 'react';
import { STUDIO_DRAWER_SEARCH_PARAM } from '@/constants/search-params';
import { copyText } from '@/lib/clipboard';
import type { Studio } from '@/types';

interface StudioDetailHeaderProps {
  studio: Studio;
  titleId: string;
  onClose: () => void;
  aiIdPrefix?: string;
}

type CopyStatus = 'idle' | 'copied' | 'failed';

export function StudioDetailHeader({ studio, titleId, onClose, aiIdPrefix = 'studio-detail-header' }: StudioDetailHeaderProps) {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  useEffect(() => {
    if (copyStatus === 'idle') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCopyStatus('idle'), 1400);
    return () => window.clearTimeout(timeoutId);
  }, [copyStatus]);

  async function handleCopyShareUrl() {
    try {
      const shareUrl = new URL(window.location.href);
      shareUrl.searchParams.set(STUDIO_DRAWER_SEARCH_PARAM, String(studio.id));
      const didCopy = await copyText(shareUrl.toString());
      setCopyStatus(didCopy ? 'copied' : 'failed');
    } catch {
      try {
        const didCopy = await copyText(studio.shareUrl);
        setCopyStatus(didCopy ? 'copied' : 'failed');
      } catch {
        setCopyStatus('failed');
      }
    }
  }

  const shareLabel = copyStatus === 'copied' ? '복사됨' : copyStatus === 'failed' ? '실패' : '공유 링크 복사';

  return (
    <header className="shrink-0 border-b border-slate-100 bg-white px-5 py-4" data-ai-id={aiIdPrefix}>
      <div className="flex items-center justify-between gap-2" data-ai-id={`${aiIdPrefix}-actions`}>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
          aria-label="연습실 상세 닫기"
          data-ai-id={`${aiIdPrefix}-actions-close-button`}
        >
          <span data-ai-id={`${aiIdPrefix}-actions-close-button-label-text`}>닫기</span>
        </button>
        <button
          type="button"
          onClick={handleCopyShareUrl}
          className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
          aria-label={`${studio.title} 공유 링크 복사`}
          data-ai-id={`${aiIdPrefix}-actions-share-copy-button`}
        >
          <span data-ai-id={`${aiIdPrefix}-actions-share-copy-button-label-text`}>{shareLabel}</span>
        </button>
      </div>

      <div className="mt-4 min-w-0" data-ai-id={`${aiIdPrefix}-heading`}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600" data-ai-id={`${aiIdPrefix}-heading-eyebrow`}>
          <span data-ai-id={`${aiIdPrefix}-heading-eyebrow-text`}>연습실 상세</span>
        </p>
        <h2 id={titleId} className="mt-1 break-keep text-2xl font-bold tracking-tight text-slate-950" data-ai-id={`${aiIdPrefix}-heading-title`}>
          <span data-ai-id={`${aiIdPrefix}-heading-title-text`}>{studio.title}</span>
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-500" data-ai-id={`${aiIdPrefix}-heading-location-summary`}>
          <span data-ai-id={`${aiIdPrefix}-heading-location-summary-text`}>{studio.location || '위치 미입력'}</span>
        </p>
      </div>
    </header>
  );
}
